import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ChevronDown,
  ExternalLink,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  type AccountChat,
  type BroadcastAccount,
  type BroadcastChat,
  type BroadcastDetail,
  type BroadcastMembership,
  type BroadcastSchedule,
  type ChatsAdded,
  accountName,
  blockerTone,
  countChatLines,
  joinDays,
  useAccountChats,
  useAddBroadcastChats,
  useAddChatsFromAccount,
  useBroadcast,
  useBroadcastLifecycle,
  useBroadcastPosts,
  useRemoveBroadcastChat,
  useRetryBroadcastChat,
  useSetBroadcastAccounts,
  useSetBroadcastMessages,
  useTestSend,
  useUpdateBroadcast,
} from "@/lib/broadcasts";
import { formatNumber, formatRelative, formatShortTime } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailDrawer } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/cn";
import { AccountPicker } from "./AccountPicker";
import { MessagesEditor } from "./MessagesEditor";
import { ForecastNote, ScheduleFields } from "./ScheduleFields";
import { useIntervalLabel } from "./useIntervalLabel";

const FIELD =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 font-mono text-sm leading-relaxed " +
  "placeholder:font-sans placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
const ICON_BUTTON =
  "shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50";

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.detail : fallback;
}

/** What adding chats came to, as one toast. */
function reportAdded(t: TFunction, out: ChatsAdded) {
  const parts = [t("broadcasts.chats.added", { count: out.added })];
  if (out.already > 0) parts.push(t("broadcasts.chats.added_already", { count: out.already }));
  if (out.unsupported.length > 0) {
    parts.push(t("broadcasts.chats.added_unsupported", { count: out.unsupported.length }));
  }
  pushToast({
    kind: out.added > 0 ? "success" : "info",
    text: parts.join(" · "),
    detail: out.unsupported.length > 0 ? t("broadcasts.chats.unsupported_hint") : undefined,
  });
}

/**
 * A broadcast's card: how it is doing, and everything about it that can be
 * changed while it runs — its chats, accounts, messages and pace — plus the
 * record of what it posted.
 */
export function BroadcastDrawer({
  id,
  open,
  onOpenChange,
  onDelete,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (broadcast: BroadcastDetail) => void;
}) {
  const { t } = useTranslation();
  const detail = useBroadcast(open ? id : null);
  const pause = useBroadcastLifecycle("pause");
  const resume = useBroadcastLifecycle("resume");
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    if (open) setTab("overview");
  }, [open, id]);

  const b = detail.data;
  const body = (render: (b: BroadcastDetail) => ReactNode) =>
    b ? (
      render(b)
    ) : (
      <p className="text-sm text-fg-muted">
        {t(detail.isError ? "broadcasts.load_error" : "broadcasts.loading")}
      </p>
    );

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={b?.name ?? "…"}
      status={
        b
          ? {
              domain: "broadcast",
              // Running but held by something reads "waiting", as in the list.
              value: b.waiting_reason ? "waiting" : b.status,
              label: t(`broadcasts.status.${b.waiting_reason ? "waiting" : b.status}`),
            }
          : undefined
      }
      activeTab={tab}
      onTabChange={setTab}
      tabs={[
        {
          value: "overview",
          label: t("broadcasts.tab.overview"),
          content: body((b) => <Overview b={b} onOpenTab={setTab} />),
        },
        {
          value: "chats",
          label: (
            <>
              {t("broadcasts.tab.chats")}
              {b && <span className="font-mono text-xs text-fg-muted">{b.chat_count}</span>}
            </>
          ),
          content: body((b) => <Chats b={b} />),
        },
        {
          value: "accounts",
          label: (
            <>
              {t("broadcasts.tab.accounts")}
              {b && <span className="font-mono text-xs text-fg-muted">{b.account_count}</span>}
            </>
          ),
          content: body((b) => <Accounts b={b} />),
        },
        {
          value: "messages",
          label: (
            <>
              {t("broadcasts.tab.messages")}
              {b && <span className="font-mono text-xs text-fg-muted">{b.message_count}</span>}
            </>
          ),
          content: body((b) => <Messages b={b} />),
        },
        {
          value: "schedule",
          label: t("broadcasts.tab.schedule"),
          content: body((b) => <Schedule key={b.id} b={b} />),
        },
        {
          value: "report",
          label: t("broadcasts.tab.report"),
          content: body((b) => <Report b={b} active={tab === "report"} />),
        },
      ]}
      footer={
        b && (
          <>
            <Button
              variant="ghost"
              className="mr-auto text-danger hover:text-danger"
              onClick={() => onDelete(b)}
              iconLeft={<Trash2 size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {t("broadcasts.delete")}
            </Button>
            {b.status === "active" ? (
              <Button
                variant="outline"
                loading={pause.isPending}
                onClick={() => pause.mutate({ id: b.id })}
                iconLeft={<Pause size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("broadcasts.pause")}
              </Button>
            ) : (
              <Button
                loading={resume.isPending}
                onClick={() => resume.mutate({ id: b.id })}
                iconLeft={<Play size={15} strokeWidth={2} aria-hidden="true" />}
              >
                {t("broadcasts.resume")}
              </Button>
            )}
          </>
        )
      }
    />
  );
}
BroadcastDrawer.displayName = "BroadcastDrawer";

/* ----------------------------------------------------------------- overview */

function Tile({
  label,
  value,
  note,
  tone,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
  tone?: "warning" | "danger";
  /** Every number opens the tab that explains it. */
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl border border-border bg-bg-primary px-4 py-3 text-left transition-colors",
        "hover:border-fg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <div className="text-xs text-fg-muted">{label}</div>
      <div
        className={cn(
          "mt-1 font-mono text-2xl font-semibold tabular-nums leading-none",
          tone === "warning" && "text-warning",
          tone === "danger" && "text-danger",
        )}
      >
        {value}
      </div>
      {note && <div className="mt-1.5 text-xs text-fg-muted">{note}</div>}
    </button>
  );
}

function Overview({ b, onOpenTab }: { b: BroadcastDetail; onOpenTab: (tab: string) => void }) {
  const { t } = useTranslation();
  const interval = useIntervalLabel();
  const held = b.accounts.filter((a) => a.blocker !== null);
  const blockedSeats = b.chats.reduce(
    (n, c) => n + c.memberships.filter((m) => m.state === "blocked").length,
    0,
  );
  const removedShare = b.posts_sent > 0 ? Math.round((b.posts_removed / b.posts_sent) * 100) : 0;

  let next: string | null = null;
  if (b.next_post_at) {
    const minutes = (Date.parse(b.next_post_at) - Date.now()) / 60_000;
    next =
      minutes < 1
        ? t("broadcasts.overview.next_soon")
        : t("broadcasts.overview.next_in", { in: interval(minutes) });
  }

  return (
    <div className="space-y-5">
      {b.status === "paused" ? (
        <Alert tone="warning" title={t("broadcasts.overview.paused_title")}>
          {t("broadcasts.overview.paused_text")}
        </Alert>
      ) : b.waiting_reason ? (
        <Alert
          tone={
            b.waiting_reason === "joining" || b.waiting_reason === "resting" ? "info" : "warning"
          }
          title={t(`broadcasts.waiting.${b.waiting_reason}`)}
        >
          {t(`broadcasts.waiting_hint.${b.waiting_reason}`)}
        </Alert>
      ) : (
        <Alert tone="success" title={t("broadcasts.overview.running_title")}>
          {next ?? t("broadcasts.overview.running_text")}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Tile
          label={t("broadcasts.overview.posts_24h")}
          value={formatNumber(b.posts_24h)}
          note={t("broadcasts.overview.posts_total", { count: b.posts_sent })}
          onClick={() => onOpenTab("report")}
        />
        <Tile
          label={t("broadcasts.overview.removed")}
          value={formatNumber(b.posts_removed)}
          tone={removedShare >= 30 ? "warning" : undefined}
          note={
            b.posts_sent > 0
              ? t("broadcasts.overview.removed_share", { share: removedShare })
              : t("broadcasts.overview.removed_none")
          }
          onClick={() => onOpenTab("chats")}
        />
        <Tile
          label={t("broadcasts.overview.chats")}
          value={`${b.chats_ready}/${b.chat_count}`}
          note={t("broadcasts.overview.chats_note")}
          onClick={() => onOpenTab("chats")}
        />
        <Tile
          label={t("broadcasts.overview.accounts")}
          value={`${b.accounts_ready}/${b.account_count}`}
          tone={b.account_count > 0 && b.accounts_ready === 0 ? "danger" : undefined}
          note={t("broadcasts.overview.accounts_note")}
          onClick={() => onOpenTab("accounts")}
        />
      </div>

      {(held.length > 0 || blockedSeats > 0) && (
        <div className="space-y-2 rounded-xl border border-border bg-bg-primary p-4">
          <p className="text-sm font-semibold text-fg-primary">
            {t("broadcasts.overview.attention")}
          </p>
          <ul className="space-y-1.5 text-sm">
            {held.map((a) => (
              <li key={a.account_id} className="flex flex-wrap items-center gap-2">
                <span className="truncate text-fg-secondary">{accountName(a)}</span>
                <Badge tone={blockerTone(a.blocker)}>{t(`broadcasts.blocker.${a.blocker}`)}</Badge>
              </li>
            ))}
            {blockedSeats > 0 && (
              <li>
                <button
                  type="button"
                  onClick={() => onOpenTab("chats")}
                  className="text-left text-accent hover:underline focus-visible:outline-none"
                >
                  {t("broadcasts.overview.blocked_seats", { count: blockedSeats })}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      <ForecastNote schedule={b} chats={b.chat_count} accounts={workingAccounts(b)} />
    </div>
  );
}

/* -------------------------------------------------------------------- chats */

/**
 * The accounts a forecast should count: the ones that post — asleep or not.
 * One held back by a ban or a spam limit carries none of the load. With nobody
 * working, the listed ones stand in, so that the arithmetic still shows.
 */
function workingAccounts(b: BroadcastDetail): number {
  const working = b.accounts.filter((a) => a.blocker === null || a.blocker === "resting").length;
  return working || b.account_count;
}

function chatName(chat: BroadcastChat, fallback: string): string {
  return chat.title ?? (chat.username ? `@${chat.username}` : fallback);
}

/** Half of the posts gone (of three or more): the chat's side takes them down. */
function isWiped(chat: BroadcastChat): boolean {
  return chat.posts_sent >= 3 && chat.posts_removed * 2 >= chat.posts_sent;
}

function isTroubled(chat: BroadcastChat): boolean {
  return chat.memberships.some((m) => m.state === "blocked") || isWiped(chat);
}

function Chats({ b }: { b: BroadcastDetail }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [onlyTrouble, setOnlyTrouble] = useState(false);
  const [removing, setRemoving] = useState<BroadcastChat | null>(null);
  const remove = useRemoveBroadcastChat();
  const accounts = useMemo(() => new Map(b.accounts.map((a) => [a.account_id, a])), [b.accounts]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return b.chats.filter(
      (c) =>
        (!onlyTrouble || isTroubled(c)) &&
        (!q || `${c.title ?? ""} ${c.username ?? ""}`.toLowerCase().includes(q)),
    );
  }, [b.chats, query, onlyTrouble]);
  const troubleCount = b.chats.filter(isTroubled).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[10rem] flex-1">
          <Search
            size={15}
            strokeWidth={2}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("broadcasts.chats.search") ?? ""}
            aria-label={t("broadcasts.chats.search")}
            className="h-9 pl-9"
          />
        </div>
        {troubleCount > 0 && (
          <Button
            size="sm"
            variant={onlyTrouble ? "secondary" : "outline"}
            onClick={() => setOnlyTrouble((v) => !v)}
          >
            {t("broadcasts.chats.only_trouble", { count: troubleCount })}
          </Button>
        )}
        <Button
          size="sm"
          onClick={() => setAdding(true)}
          iconLeft={<Plus size={15} strokeWidth={2.5} aria-hidden="true" />}
        >
          {t("broadcasts.chats.add")}
        </Button>
      </div>

      {b.chats.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-fg-muted">
          {t("broadcasts.chats.empty")}
        </p>
      ) : shown.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-fg-muted">
          {t("broadcasts.chats.nothing_found")}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-bg-primary">
          {shown.map((chat) => (
            <ChatRow
              key={chat.id}
              broadcastId={b.id}
              chat={chat}
              accounts={accounts}
              open={openId === chat.id}
              onToggle={() => setOpenId((cur) => (cur === chat.id ? null : chat.id))}
              onRemove={() => setRemoving(chat)}
            />
          ))}
        </ul>
      )}

      <AddChatsDialog b={b} open={adding} onOpenChange={setAdding} />

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        tone="danger"
        title={t("broadcasts.chats.remove_confirm", {
          name: removing ? chatName(removing, t("broadcasts.chats.by_invite")) : "",
        })}
        description={t("broadcasts.chats.remove_confirm_hint")}
        confirmText={t("broadcasts.chats.remove_short")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (removing) await remove.mutateAsync({ id: b.id, chat_id: removing.id });
        }}
      />
    </div>
  );
}

function ChatRow({
  broadcastId,
  chat,
  accounts,
  open,
  onToggle,
  onRemove,
}: {
  broadcastId: string;
  chat: BroadcastChat;
  accounts: Map<string, BroadcastAccount>;
  open: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const retry = useRetryBroadcastChat();
  const count = (state: BroadcastMembership["state"]) =>
    chat.memberships.filter((m) => m.state === state).length;
  const joined = count("joined");
  const waiting = count("pending") + count("requested");
  const blocked = count("blocked");
  const wiped = isWiped(chat);

  return (
    <li>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none"
        >
          <ChevronDown
            size={15}
            strokeWidth={2}
            aria-hidden="true"
            className={cn("shrink-0 text-fg-muted transition-transform", open && "rotate-180")}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-fg-primary">
              {chatName(chat, t("broadcasts.chats.by_invite"))}
            </span>
            <span className="block truncate text-xs text-fg-muted">
              {[
                chat.username && chat.title ? `@${chat.username}` : null,
                chat.by_invite && chat.title ? t("broadcasts.chats.by_invite") : null,
                t("broadcasts.chats.posts", { count: chat.posts_sent }),
                chat.last_post_at ? formatRelative(chat.last_post_at) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        </button>
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          {wiped && (
            <Badge tone="warning" title={t("broadcasts.chats.wiped_hint")}>
              {t("broadcasts.chats.wiped", { removed: chat.posts_removed, sent: chat.posts_sent })}
            </Badge>
          )}
          {joined > 0 && (
            <Badge tone="success">{t("broadcasts.chats.joined", { count: joined })}</Badge>
          )}
          {waiting > 0 && (
            <Badge tone="info" title={t("broadcasts.seat.joining")}>
              {t("broadcasts.chats.waiting", { count: waiting })}
            </Badge>
          )}
          {blocked > 0 && (
            <Badge tone="danger" title={t("broadcasts.seat.blocked")}>
              {t("broadcasts.chats.blocked", { count: blocked })}
            </Badge>
          )}
        </span>
        {chat.last_post_url && (
          <a
            href={chat.last_post_url}
            target="_blank"
            rel="noreferrer"
            aria-label={t("broadcasts.chats.open_post")}
            title={t("broadcasts.chats.open_post")}
            className={ICON_BUTTON}
          >
            <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
          </a>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label={t("broadcasts.chats.remove")}
          title={t("broadcasts.chats.remove")}
          className={cn(ICON_BUTTON, "hover:text-danger")}
        >
          <X size={15} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="space-y-2 border-t border-border bg-bg-secondary/50 px-3 py-3">
          {chat.memberships.length === 0 ? (
            <p className="text-xs text-fg-muted">{t("broadcasts.chats.no_accounts")}</p>
          ) : (
            <ul className="space-y-1.5">
              {chat.memberships.map((m) => {
                const account = accounts.get(m.account_id);
                const waits = m.not_before && Date.parse(m.not_before) > Date.now();
                return (
                  <li key={m.account_id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <span className="min-w-0 max-w-[14rem] truncate text-fg-secondary">
                      {account ? accountName(account) : "—"}
                    </span>
                    <StatusBadge
                      domain="membership"
                      value={m.state}
                      label={t(`broadcasts.seat.${m.state}`)}
                    />
                    {m.reason && (
                      <span
                        className={m.state === "blocked" ? "text-danger" : "text-warning"}
                        title={m.detail ?? undefined}
                      >
                        {t(`broadcasts.reason.${m.reason}`, { defaultValue: m.reason })}
                      </span>
                    )}
                    {m.state === "joined" && waits && (
                      <span className="text-fg-muted">
                        {t("broadcasts.seat.not_before", { when: formatShortTime(m.not_before!) })}
                      </span>
                    )}
                    {m.posts_sent > 0 && (
                      <span className="ml-auto text-fg-muted">
                        {t("broadcasts.chats.posts", { count: m.posts_sent })}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {blocked > 0 && (
            <Button
              size="sm"
              variant="outline"
              loading={retry.isPending}
              onClick={() =>
                retry.mutate(
                  { id: broadcastId, chat_id: chat.id },
                  {
                    onSuccess: () =>
                      pushToast({ kind: "success", text: t("broadcasts.chats.retry_done") }),
                  },
                )
              }
              iconLeft={<RotateCcw size={14} strokeWidth={2} aria-hidden="true" />}
            >
              {t("broadcasts.chats.retry")}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function AddChatsDialog({
  b,
  open,
  onOpenChange,
}: {
  b: BroadcastDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [way, setWay] = useState("links");
  const [text, setText] = useState("");
  const [accountId, setAccountId] = useState<string>("");
  const [found, setFound] = useState<AccountChat[] | null>(null);
  const [ticked, setTicked] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const addLinks = useAddBroadcastChats();
  const read = useAccountChats();
  const addFound = useAddChatsFromAccount();

  // Every opening starts clean. Only on opening: the card behind re-reads
  // itself on a beat, and that must not wipe what is being typed here.
  useEffect(() => {
    if (!open) return;
    setText("");
    setFound(null);
    setTicked([]);
    setError(null);
    setAccountId((current) => current || (b.accounts[0]?.account_id ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lines = countChatLines(text);
  const fresh = (found ?? []).filter((c) => !c.already);

  const submitLinks = async () => {
    setError(null);
    try {
      reportAdded(t, await addLinks.mutateAsync({ id: b.id, text }));
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, t("broadcasts.action_error")));
    }
  };

  const readChats = async () => {
    setError(null);
    setFound(null);
    setTicked([]);
    try {
      setFound(await read.mutateAsync({ id: b.id, account_id: accountId }));
    } catch (err) {
      setError(errorText(err, t("broadcasts.action_error")));
    }
  };

  const submitFound = async () => {
    setError(null);
    try {
      reportAdded(
        t,
        await addFound.mutateAsync({ id: b.id, account_id: accountId, tg_chat_ids: ticked }),
      );
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, t("broadcasts.action_error")));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("broadcasts.chats.add_title")}</DialogTitle>
          <DialogDescription>{t("broadcasts.chats.add_subtitle")}</DialogDescription>
          <Tabs value={way} onValueChange={setWay} variant="pill" className="mt-3">
            <TabsList>
              <TabsTrigger value="links">{t("broadcasts.chats.way_links")}</TabsTrigger>
              <TabsTrigger value="account" disabled={b.accounts.length === 0}>
                {t("broadcasts.chats.way_account")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {way === "links" ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={9}
                autoFocus
                aria-label={t("broadcasts.field.chats")}
                placeholder={"https://t.me/job_board\n@another_board\nhttps://t.me/+AbCdEfGhIjKl"}
                className={cn(FIELD, "min-h-[11rem] resize-y")}
              />
              <p className="text-xs text-fg-muted">{t("broadcasts.chats.paste_hint")}</p>
              <p className="text-sm text-fg-secondary">
                {t("broadcasts.chats.recognised", { count: lines })}
                {lines > 0 && (
                  <span className="text-fg-muted">
                    {" · "}
                    {t("broadcasts.chats.join_eta", { count: joinDays(lines) })}
                  </span>
                )}
              </p>
            </>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-fg-muted">
                {t("broadcasts.chats.account_hint")}
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[12rem] flex-1 space-y-1.5">
                  <Label className="text-xs text-fg-secondary">
                    {t("broadcasts.chats.account_label")}
                  </Label>
                  <SimpleSelect
                    value={accountId}
                    onValueChange={(v) => {
                      setAccountId(v);
                      setFound(null);
                      setTicked([]);
                    }}
                    options={b.accounts.map((a) => ({
                      value: a.account_id,
                      label: accountName(a),
                    }))}
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!accountId}
                  loading={read.isPending}
                  onClick={readChats}
                >
                  {t("broadcasts.chats.read")}
                </Button>
              </div>
              {read.isPending && (
                <p className="text-xs text-fg-muted">{t("broadcasts.chats.reading")}</p>
              )}
              {found && found.length === 0 && (
                <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-fg-muted">
                  {t("broadcasts.chats.account_none")}
                </p>
              )}
              {found && found.length > 0 && (
                <>
                  <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
                    <span>{t("broadcasts.chats.account_found", { count: found.length })}</span>
                    <button
                      type="button"
                      disabled={fresh.length === 0}
                      onClick={() =>
                        setTicked(
                          ticked.length === fresh.length ? [] : fresh.map((c) => c.tg_chat_id),
                        )
                      }
                      className="text-accent hover:underline disabled:opacity-50"
                    >
                      {ticked.length === fresh.length && fresh.length > 0
                        ? t("broadcasts.chats.untick_all")
                        : t("broadcasts.chats.tick_all")}
                    </button>
                  </div>
                  <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-bg-primary">
                    {found.map((c) => (
                      <li key={c.tg_chat_id}>
                        <label
                          className={cn(
                            "flex items-center gap-2 px-3 py-2",
                            c.already ? "opacity-60" : "cursor-pointer hover:bg-bg-secondary",
                          )}
                        >
                          <Checkbox
                            disabled={c.already}
                            checked={c.already || ticked.includes(c.tg_chat_id)}
                            onCheckedChange={() =>
                              setTicked((cur) =>
                                cur.includes(c.tg_chat_id)
                                  ? cur.filter((x) => x !== c.tg_chat_id)
                                  : [...cur, c.tg_chat_id],
                              )
                            }
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-fg-primary">{c.title}</span>
                            <span className="block truncate text-xs text-fg-muted">
                              {[
                                c.username ? `@${c.username}` : t("broadcasts.chats.closed"),
                                c.members !== null
                                  ? t("broadcasts.chats.members", { count: c.members })
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </span>
                          {c.already && <Badge>{t("broadcasts.chats.already_in")}</Badge>}
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          {way === "links" ? (
            <Button disabled={lines === 0} loading={addLinks.isPending} onClick={submitLinks}>
              {t("broadcasts.chats.add_n", { count: lines })}
            </Button>
          ) : (
            <Button
              disabled={ticked.length === 0}
              loading={addFound.isPending}
              onClick={submitFound}
            >
              {t("broadcasts.chats.add_n", { count: ticked.length })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------------------------------------------- accounts */

function Accounts({ b }: { b: BroadcastDetail }) {
  const { t } = useTranslation();
  const save = useSetBroadcastAccounts();
  // As a string: the card re-reads itself on a beat, and a fresh array with
  // the same accounts in it must not throw away a choice being made.
  const currentKey = b.accounts.map((a) => a.account_id).join(",");
  const current = useMemo(() => (currentKey ? currentKey.split(",") : []), [currentKey]);
  const [draft, setDraft] = useState<string[]>(current);
  useEffect(() => setDraft(current), [current]);
  const dirty =
    draft.length !== current.length || draft.some((id) => !current.includes(id));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <AccountPicker selected={draft} onChange={setDraft} disabled={save.isPending} />
        {dirty && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { id: b.id, account_ids: draft },
                  {
                    onSuccess: () =>
                      pushToast({ kind: "success", text: t("broadcasts.accounts.saved") }),
                  },
                )
              }
            >
              {t("broadcasts.accounts.save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDraft(current)}>
              {t("common.cancel")}
            </Button>
            <span className="text-xs text-fg-muted">{t("broadcasts.accounts.save_hint")}</span>
          </div>
        )}
        <p className="text-xs leading-relaxed text-fg-muted">
          {t("broadcasts.accounts.gate_hint")}
        </p>
      </div>

      {b.accounts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-fg-muted">
          {t("broadcasts.accounts.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-bg-primary">
          {b.accounts.map((a) => (
            <li key={a.account_id} className="space-y-1.5 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg-primary">
                  {accountName(a)}
                </span>
                <Badge tone={blockerTone(a.blocker)} dot>
                  {a.blocker === null
                    ? t("broadcasts.blocker.ready")
                    : t(`broadcasts.blocker.${a.blocker}`)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
                <span>{t("broadcasts.accounts.in_chats", { count: a.chats_joined })}</span>
                {a.chats_pending + a.chats_requested > 0 && (
                  <span>
                    {/* Held back, it is not joining anything: no "in N days". */}
                    {a.blocker === null || a.blocker === "resting"
                      ? t("broadcasts.accounts.joining", {
                          count: a.chats_pending + a.chats_requested,
                          days: Math.max(1, joinDays(a.chats_pending)),
                        })
                      : t("broadcasts.accounts.to_join", {
                          count: a.chats_pending + a.chats_requested,
                        })}
                  </span>
                )}
                {a.chats_blocked > 0 && (
                  <span className="text-danger">
                    {t("broadcasts.accounts.blocked_in", { count: a.chats_blocked })}
                  </span>
                )}
                <span>{t("broadcasts.chats.posts", { count: a.posts_sent })}</span>
                {a.last_post_at && <span>{formatRelative(a.last_post_at)}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- messages */

function Messages({ b }: { b: BroadcastDetail }) {
  const { t } = useTranslation();
  const save = useSetBroadcastMessages();
  const test = useTestSend();
  const [accountId, setAccountId] = useState("");
  useEffect(() => {
    if (!b.accounts.some((a) => a.account_id === accountId)) {
      setAccountId(b.accounts[0]?.account_id ?? "");
    }
  }, [b.accounts, accountId]);

  const set = (ids: string[]) => save.mutate({ id: b.id, material_ids: ids });

  return (
    <div className="space-y-4">
      <MessagesEditor
        messages={b.messages}
        disabled={save.isPending}
        onAdd={(m) => set([...b.messages.map((x) => x.id), m.id])}
        onRemove={(id) => set(b.messages.filter((x) => x.id !== id).map((x) => x.id))}
        rowAction={(m) =>
          accountId ? (
            <button
              type="button"
              disabled={test.isPending}
              aria-label={t("broadcasts.messages.test")}
              title={t("broadcasts.messages.test")}
              className={ICON_BUTTON}
              onClick={() =>
                test.mutate(
                  { id: b.id, account_id: accountId, material_id: m.id },
                  {
                    onSuccess: () =>
                      pushToast({ kind: "success", text: t("broadcasts.messages.test_done") }),
                  },
                )
              }
            >
              <Send size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          ) : null
        }
      />

      {b.messages.length > 0 && b.accounts.length > 0 && (
        <div className="space-y-1.5 rounded-lg border border-border bg-bg-primary p-3">
          <Label className="text-xs text-fg-secondary">{t("broadcasts.messages.test_from")}</Label>
          <SimpleSelect
            value={accountId}
            onValueChange={setAccountId}
            options={b.accounts.map((a) => ({ value: a.account_id, label: accountName(a) }))}
          />
          <p className="text-xs leading-relaxed text-fg-muted">
            {t("broadcasts.messages.test_hint")}
          </p>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- schedule */

function Schedule({ b }: { b: BroadcastDetail }) {
  const { t } = useTranslation();
  const save = useUpdateBroadcast();
  const saved: BroadcastSchedule = {
    mode: b.mode,
    interval_minutes: b.interval_minutes,
    gap_min_seconds: b.gap_min_seconds,
    gap_max_seconds: b.gap_max_seconds,
    delete_previous: b.delete_previous,
  };
  const [name, setName] = useState(b.name);
  const [draft, setDraft] = useState<BroadcastSchedule>(saved);
  const dirty =
    name.trim() !== b.name ||
    (Object.keys(saved) as (keyof BroadcastSchedule)[]).some((k) => draft[k] !== saved[k]);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="bc_edit_name">{t("broadcasts.field.name")}</Label>
        <Input
          id="bc_edit_name"
          value={name}
          maxLength={128}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <ScheduleFields
        value={draft}
        onChange={setDraft}
        chats={b.chat_count}
        accounts={workingAccounts(b)}
        disabled={save.isPending}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={!dirty || !name.trim()}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              { id: b.id, name: name.trim(), ...draft },
              {
                onSuccess: (kept) => {
                  // As the server keeps it (it folds runs of spaces) — or the
                  // form would go on reading "changed".
                  setName(kept.name);
                  pushToast({ kind: "success", text: t("broadcasts.schedule.saved") });
                },
              },
            )
          }
        >
          {t("broadcasts.schedule.save")}
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            onClick={() => {
              setName(b.name);
              setDraft(saved);
            }}
          >
            {t("common.cancel")}
          </Button>
        )}
        <span className="text-xs text-fg-muted">{t("broadcasts.schedule.save_hint")}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- report */

const REPORT_FILTERS = ["all", "failed", "removed"] as const;

function Report({ b, active }: { b: BroadcastDetail; active: boolean }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<(typeof REPORT_FILTERS)[number]>("all");
  const posts = useBroadcastPosts(
    active ? b.id : null,
    { only: filter === "all" ? undefined : filter, limit: 100 },
    b.status === "active",
  );
  const items = posts.data?.items ?? [];

  return (
    <div className="space-y-3">
      <Tabs
        value={filter}
        onValueChange={(v) => setFilter(v as (typeof REPORT_FILTERS)[number])}
        variant="pill"
      >
        <TabsList>
          {REPORT_FILTERS.map((f) => (
            <TabsTrigger key={f} value={f}>
              {t(`broadcasts.report.filter_${f}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {!posts.data ? (
        <p className="text-sm text-fg-muted">
          {t(posts.isError ? "broadcasts.load_error" : "broadcasts.loading")}
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-fg-muted">
          {t(filter === "all" ? "broadcasts.report.empty" : "broadcasts.report.empty_filtered")}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-xl border border-border bg-bg-primary">
            {items.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                <span className="w-14 shrink-0 font-mono text-xs tabular-nums text-fg-muted">
                  {formatShortTime(p.sent_at)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-fg-primary">
                    {p.chat_title ?? t("broadcasts.chats.by_invite")}
                  </span>
                  <span className="block truncate text-xs text-fg-muted">
                    {p.account_label ?? t("broadcasts.report.account_gone")}
                  </span>
                </span>
                {p.status === "failed" ? (
                  <Badge tone="danger" title={p.detail ?? undefined}>
                    {t(`broadcasts.reason.${p.error}`, { defaultValue: p.error ?? "" })}
                  </Badge>
                ) : p.removed_at ? (
                  <Badge tone="warning" title={t("broadcasts.report.removed_hint")}>
                    {t("broadcasts.report.removed")}
                  </Badge>
                ) : (
                  <Badge tone="success">{t("broadcasts.report.sent")}</Badge>
                )}
                {p.url ? (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t("broadcasts.chats.open_post")}
                    title={t("broadcasts.chats.open_post")}
                    className={ICON_BUTTON}
                  >
                    <ExternalLink size={15} strokeWidth={2} aria-hidden="true" />
                  </a>
                ) : (
                  <span className="w-[27px] shrink-0" aria-hidden="true" />
                )}
              </li>
            ))}
          </ul>
          {posts.data.total > items.length && (
            <p className="text-center text-xs text-fg-muted">
              {t("broadcasts.report.showing", { shown: items.length, total: posts.data.total })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
